import type {
  AiCampaignForm,
  AiCampaignStepKey,
  CampaignTypeValue,
  ChoiceOption,
  CustomOptionValue,
  ToneKey,
} from "./AiCampaignTypes";

const EDITOR_KEY = "campaignFlow.aiEmailEditor";

export const CUSTOM_OPTION: CustomOptionValue = "Custom";

export const AI_CAMPAIGN_STEPS: { key: AiCampaignStepKey; titleKey: string }[] = [
  { key: "productCategories", titleKey: "campaignFlow.aiWizard.steps.productCategories" },
  { key: "campaignCategory", titleKey: "campaignFlow.aiWizard.steps.campaignCategory" },
  { key: "campaignSubCategory", titleKey: "campaignFlow.aiWizard.steps.campaignSubCategory" },
  { key: "aiPrompt", titleKey: "campaignFlow.aiWizard.steps.aiPrompt" },
  { key: "senderRecipients", titleKey: "campaignFlow.aiWizard.steps.senderRecipients" },
  { key: "createCampaign", titleKey: "campaignFlow.aiWizard.steps.createCampaign" },
];

export const EMAIL_WIZARD_STEPS: { key: AiCampaignStepKey; titleKey: string }[] = [
  { key: "campaignDetails", titleKey: "campaignFlow.aiWizard.steps.campaignDetails" },
  ...AI_CAMPAIGN_STEPS.filter(step => step.key !== "createCampaign"),
  { key: "reviewDetails", titleKey: "campaignFlow.aiWizard.steps.reviewDetails" },
  { key: "createCampaign", titleKey: "campaignFlow.aiWizard.steps.aiCampaign" },
];

export const MAX_CAMPAIGN_NAME_LENGTH = 100;
export const MAX_PRODUCT_CATEGORIES = 5;
export const MAX_SUBJECT_LENGTH = 100;

export const CAMPAIGN_TYPE_OPTIONS: ChoiceOption<CampaignTypeValue>[] = [
  {
    value: "Customer Lifecycle Campaigns",
    titleKey: `${EDITOR_KEY}.campaignTypes.customerLifecycle`,
  },
  {
    value: "Informational & Brand Content",
    titleKey: `${EDITOR_KEY}.campaignTypes.informationalBrand`,
  },
  { value: "Promotional Campaigns", titleKey: `${EDITOR_KEY}.campaignTypes.promotional` },
  { value: "Behavioral Campaigns", titleKey: `${EDITOR_KEY}.campaignTypes.behavioral` },
];

const subCampaign = (value: string, key: string): ChoiceOption => ({
  value,
  titleKey: `${EDITOR_KEY}.subCampaigns.${key}.title`,
  descKey: `${EDITOR_KEY}.subCampaigns.${key}.desc`,
});

export const SUB_CAMPAIGN_OPTIONS: Record<CampaignTypeValue, ChoiceOption[]> = {
  "Customer Lifecycle Campaigns": [
    subCampaign("Welcome Series", "welcomeSeries"),
    subCampaign("Abandoned Cart Recovery", "abandonedCartRecovery"),
    subCampaign("Browse Abandonment", "browseAbandonment"),
    subCampaign("Post Purchase Follow Up", "postPurchaseFollowUp"),
    subCampaign("Behavioral", "behavioralReminder"),
    subCampaign("Win Back Campaigns", "winBackCampaigns"),
  ],
  "Informational & Brand Content": [
    subCampaign("Brand Storytelling Series", "brandStorytellingSeries"),
    subCampaign("Style Guides / Lookbooks", "styleGuidesLookbooks"),
    subCampaign("How To Content", "howToContent"),
    subCampaign("Newsletter or Product Digest", "newsletterProductDigest"),
  ],
  "Promotional Campaigns": [
    subCampaign("Flash Sale Alerts", "flashSaleAlerts"),
    subCampaign("Seasonal/Holiday Promotions", "seasonalHolidayPromotions"),
    subCampaign("New Product Launches", "newProductLaunches"),
    subCampaign("Exclusive VIP Offers", "exclusiveVipOffers"),
    subCampaign("Price Drop Alerts", "priceDropAlerts"),
  ],
  "Behavioral Campaigns": [
    subCampaign("Product Recommendation Emails", "productRecommendationEmails"),
    subCampaign("Birthday or Anniversary Emails", "birthdayOrAnniversaryEmails"),
    subCampaign("Review & UGC Requests", "reviewUgcRequests"),
  ],
};

export const TONE_KEYS: ToneKey[] = ["friendly", "professional", "urgent", "playful", "informative"];

export const getToneLabelKey = (tone: ToneKey) => `${EDITOR_KEY}.tones.${tone}`;

export const CATEGORIES_PAGE_SIZE = 15;
export const VISIBLE_CATEGORY_TAGS = 5;
export const EDITOR_MIN_HEIGHT = 520;

export const DEFAULT_FROM_EMAIL = "marketing@datatram.ai";
export const DEFAULT_REPLY_TO = "support@datatram.com";
export const CREATE_CAMPAIGN_ROUTE = "/account/engagement/create-campaign";

export const createInitialAiCampaignForm = (campaignName: string): AiCampaignForm => ({
  selectedCategories: [],
  campaignType: null,
  customCampaignType: "",
  subCampaign: null,
  customSubCampaign: "",
  prompt: "",
  tone: null,
  persona: "",
  seasonalTag: "",
  offer: "",
  ctaUrl: "",
  ctaLabel: "",
  campaignName,
  subjectLine: "",
});
